import { Bell, CreditCard, Lock, Settings, Shield, User } from "lucide-react";
import { useState } from "react";
import AccountInfo from "../../components/accountsettings/AccountInfo";
import BillingAndSubscriptions from "../../components/accountsettings/BillingAndSubscriptions";
import NotificationSettings from "../../components/accountsettings/NotificationSettings";
import Placeholder from "../../components/accountsettings/Placeholder";
import SecuritySettings from "../../components/accountsettings/SecuritySettings";
import { useAuthStore } from "../../store/authStore.js";

const AccountSettingsPage = () => {
  const user = useAuthStore(s => s.user);
  const [activeTab, setActiveTab] = useState("info");

  const tabs = [
    { id: "info", name: "Account Info", icon: User },
    { id: "security", name: "Security", icon: Lock },
    { id: "notifications", name: "Notifications", icon: Bell },
    { id: "billing", name: "Billing & Subscriptions", icon: CreditCard },
    { id: "privacy", name: "Privacy", icon: Shield },
    { id: "preferences", name: "Preferences", icon: Settings },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case "info":
        return <AccountInfo userRole={user?.role} />;
      case "security":
        return <SecuritySettings />;
      case "notifications":
        return <NotificationSettings />;
      case "billing":
        return <BillingAndSubscriptions />;
      case "privacy":
        return (
          <Placeholder
            title="Privacy Controls"
            message="Control how your data is used and seen by others on the platform."
          />
        );
      case "preferences":
        return (
          <Placeholder
            title="Language & Region"
            message="Set your preferred language and region for a tailored experience."
          />
        );
      default:
        return <AccountInfo userRole={user?.role} />;
    }
  };

  return (
    <div className="bg-secondary-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary-900">
            Account Settings
          </h1>
          <p className="mt-1 text-secondary-600">
            Manage your account preferences, security settings, and privacy
            controls.
          </p>
        </div>
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          <aside className="w-full lg:w-1/4 lg:sticky top-24">
            <div className="bg-white p-3 rounded-xl shadow-lg border border-gray-100 hidden lg:block">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left font-semibold text-sm transition-colors ${
                    activeTab === tab.id
                      ? "bg-primary-50 text-primary-600"
                      : "text-secondary-700 hover:bg-secondary-100"
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </div>
            <div className="lg:hidden">
              <div className="border-b border-secondary-200">
                <nav className="-mb-px flex space-x-6 overflow-x-auto">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-shrink-0 flex items-center space-x-2 py-3 px-1 text-sm font-semibold transition-colors ${
                        activeTab === tab.id
                          ? "border-b-2 border-primary-600 text-primary-600"
                          : "text-secondary-500 hover:text-secondary-700 border-b-2 border-transparent"
                      }`}
                    >
                      <tab.icon className="w-5 h-5" />
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </aside>

          <main className="w-full lg:w-3/4">{renderContent()}</main>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
